import type { GuideId, Guide, GuideCopy } from '../content';

const secretHitlerRules = {
  label: 'Официальные правила Secret Hitler',
  url: 'https://www.secrethitler.com/assets/Secret_Hitler_Rules.pdf',
};
const secretHitlerOnline = { label: 'Secret Hitler.io — игра в браузере', url: 'https://secrethitler.io/' };
const werewolfRules = {
  label: 'Ultimate Werewolf от Bézier Games',
  url: 'https://beziergames.com/products/ultimate-werewolf',
};
const oneNight = {
  label: 'One Night Ultimate Werewolf от Bézier Games',
  url: 'https://beziergames.com/products/one-night-ultimate-werewolf',
};
const browserWerewolf = { label: 'Werewolf на BoardGameJoin', url: 'https://boardgamejoin.com/en/games/werewolf/' };
const amongUs = { label: 'Among Us от Innersloth', url: 'https://www.innersloth.com/games/among-us/' };

export const guides: Record<GuideId, Guide> = {
  social_deduction_games_online: {
    slug: 'social-deduction-games-online',
    breadcrumb: 'Игры на социальную дедукцию',
    title: 'Социальная дедукция: что выбрать после «Мафии»',
    lead: 'Что попробовать после «Мафии» онлайн? Выбирайте Авалон для миссий без раннего выбывания, Secret Hitler — для законов, «Оборотней» — для ночных ролей. Эти игры на социальную дедукцию доступны в браузере для 5–10 друзей; Among Us нужно установить.',
    preview: true,
    table: {
      caption: 'Выберите игру для следующей встречи с друзьями',
      columns: ['Игра и число игроков', 'Ради чего выбрать', 'Практические отличия'],
      rows: [
        {
          title: 'Авалон · 5–10 человек или игра одному / с друзьями и ботами',
          cells: [
            'Обсуждение, открытое голосование и тайный саботаж миссий.',
            'Обычные партии для 5–10 человек с аккаунтами бесплатны. Играйте одному или пригласите друзей, а свободные места займут боты; доступное число мест и режим выбираются при настройке. Во время миссий никто не выбывает.',
          ],
        },
        {
          title: 'Secret Hitler · 5–10',
          cells: [
            'Выбор правительства и споры о том, что произошло с картами законов.',
            'В браузере можно играть на Secret Hitler.io: там есть аккаунты и возможность создавать закрытые партии. Казни могут выводить игроков из игры.',
          ],
          source: secretHitlerOnline,
        },
        {
          title: '«Оборотни» (Werewolf) · 4–30 на BoardGameJoin',
          cells: [
            'Классическая смена дня и ночи, обвинения и особые роли.',
            'Эта браузерная версия подходит в том числе для 5–10 игроков. Она обрабатывает ночные действия и голоса, а ведущий переключает фазы; игроки могут выбывать.',
          ],
          source: browserWerewolf,
        },
        {
          title: 'Among Us · 4–15',
          cells: [
            'Перемещения, выполнение заданий и построение алиби.',
            'Игру нужно установить. До встречи проверьте, на каких платформах играют друзья и какие настройки чата им доступны.',
          ],
          source: amongUs,
        },
      ],
    },
    sections: [
      {
        title: 'Что попробовать после «Мафии» онлайн с друзьями?',
        paragraphs: [
          'Выбирайте Авалон, если вам нравится снова и снова проверять, кому можно доверять. Все голосуют за предложенную команду, но только её участники тайно разыгрывают карты миссии. Провал сужает круг подозреваемых до участников команды; успешная миссия тоже может включать злого игрока, который решил не саботировать её.',
          'Выбирайте Secret Hitler, если вашей компании интересны противоречивые рассказы о тайном обмене картами. Президент и канцлер выбирают закон из вытянутых карт, поэтому важны и результат, и их объяснения. Колода законов добавляет случайность, которой нет у карт миссии в базовом Авалоне.',
        ],
        source: secretHitlerRules,
      },
      {
        title: '«Оборотни» — для ночных действий, Among Us — для перемещений',
        paragraphs: [
          'Классические «Оборотни» и «Мафия» строятся вокруг исключения подозреваемых и анализа ночных действий. Для игры на расстоянии выберите сервис, который поддерживает нужные правила, или договоритесь, кто будет ведущим. Указанная выше версия BoardGameJoin автоматизирует тайные действия, но фазы по-прежнему переключает ведущий.',
          'В Among Us между обсуждениями есть активная игра: члены экипажа выполняют задания, а предатели саботируют и убивают. На собраниях увиденное превращается в обвинения. Игра подойдёт друзьям, которым хочется чем-то заниматься между спорами, а Авалон сосредоточивает всех на общем решении о миссии.',
        ],
        source: amongUs,
      },
      {
        title: 'В каких играх нет раннего выбывания?',
        paragraphs: [
          'В Авалоне каждый игрок участвует в обсуждении и каждом голосовании за команду на протяжении всех миссий. Если вас не взяли на миссию, это временно: вы всё равно голосуете, видите результат и можете попасть в следующую команду. Убийство — решение в конце игры, а не череда выбываний, после которых игроки сидят без дела до конца встречи.',
          'Не все игры об оборотнях предполагают выбывание. One Night Ultimate Werewolf — отдельная игра для 3–10 человек: одна ночь, одно обсуждение и никакого раннего выбывания. Браузерное приложение издателя помогает озвучивать ночную фазу, но само по себе не заменяет полноценную комнату для совместной игры на расстоянии.',
        ],
        source: oneNight,
      },
      {
        title: 'Как организовать онлайн-игру с друзьями',
        paragraphs: [
          'До отправки ссылки посчитайте, кто действительно будет играть, выберите общий язык и договоритесь, как будете общаться. Для игры в Авалон здесь каждому другу нужны собственный аккаунт и устройство. Начните со знакомых ролей, а дополнительные роли и расширения оставьте на следующую партию.',
          'Если готовы меньше пяти человек, можно сыграть одному или пригласить друзей, а свободные места займут боты. Для обычной партии с людьми договоритесь о другом времени или найдите ещё игроков. Доски игроков и групп помогают связаться друг с другом; они не заполняют комнату автоматически и не обещают немедленно найти вам игру.',
        ],
      },
      {
        title: 'Можно ли освоить Авалон одному с ботами?',
        paragraphs: [
          'Чтобы попробовать миссии, необязательно собирать полную компанию. Играя одному с ботами, вы сможете предлагать команды, голосовать и видеть результаты миссий; режим «Первая игра» поможет разобраться с подсказками. Можно также пригласить друзей и заполнить свободные места ботами, выбрав число мест и режим при настройке.',
          'В партии с ботами разберитесь, как знания Мерлина и финальное убийство связаны с миссиями, а затем объясните эти решения друзьям. В настройках игры с AI в лобби видно, какие режимы и какое число мест доступны сейчас.',
        ],
      },
      {
        title: 'Игры в браузере без скачивания: что понадобится?',
        paragraphs: [
          'Для Авалона здесь каждый открывает комнату в браузере на своём телефоне, планшете или компьютере; приложение устанавливать не нужно. Обычная партия для 5–10 человек с аккаунтами бесплатна. Отправьте друзьям ссылку на комнату и держите тайные сведения о ролях на отдельных экранах. Для Among Us из этого сравнения по-прежнему нужна установленная игра.',
          'Если не все друзья готовы, можно попробовать Авалон одному или пригласить друзей и заполнить свободные места ботами. Для игры с ботами тоже нужен аккаунт. В настройках проверьте доступные режимы, языки обсуждения и число мест, включая варианты с Premium: бесплатные обычные партии с людьми не означают, что все варианты игры с AI бесплатны или всегда доступны.',
        ],
      },
    ],
  },
  games_like_secret_hitler: {
    slug: 'games-like-secret-hitler',
    breadcrumb: 'Игры, похожие на Secret Hitler',
    title: 'Игры, похожие на Secret Hitler: Авалон онлайн',
    lead: 'Ищете игры, похожие на Secret Hitler («Тайный Гитлер»)? В Авалоне 5–10 друзей голосуют за команды, а карты миссий заменяют законы. Все участвуют до конца миссий; играть можно в браузере.',
    table: {
      caption: 'Secret Hitler и Авалон: как меняются решения',
      columns: ['Решение', 'Secret Hitler', 'Авалон'],
      rows: [
        {
          title: 'Что вы одобряете',
          cells: [
            'Президента и предложенного им канцлера.',
            'Команду на миссию, размер которой зависит от номера миссии и числа игроков.',
          ],
        },
        {
          title: 'Тайное действие',
          cells: [
            'Избранная пара вытягивает и сбрасывает карты законов.',
            'Каждый участник выбранной команды тайно разыгрывает карту миссии.',
          ],
        },
        {
          title: 'Чем можно объяснить плохой результат?',
          cells: [
            'Выбором закона и тем, какие карты были вытянуты.',
            'В базовой игре — тем, что хотя бы один злой участник команды выбрал «Провал».',
          ],
        },
        {
          title: 'Участие в игре',
          cells: [
            'Казни могут вывести игроков из партии до её конца.',
            'Все продолжают обсуждать и голосовать на протяжении всех миссий.',
          ],
        },
        {
          title: 'Особый финал',
          cells: [
            'Избрание Гитлера канцлером после принятия трёх фашистских законов может завершить игру.',
            'После трёх успешных миссий зло всё ещё может победить, определив Мерлина.',
          ],
        },
      ],
    },
    sections: [
      {
        title: 'Чем отличаются голосование и тайные карты?',
        paragraphs: [
          'В обеих играх предложение должно получить одобрение большинства, а равенство голосов означает отказ. Следить за тем, кто кого поддерживал, по-прежнему полезно. В Авалоне спрашивайте, почему игрок поддерживает всю команду, а не обсуждайте только её лидера.',
          'В Secret Hitler даже верным либералам может не достаться либерального закона для принятия. В базовом Авалоне общей колоды законов нет: добрые участники миссии обязаны выбирать «Успех», а злые могут выбрать «Успех» или «Провал». Поэтому «Провал» даёт информацию о команде, хотя и не раскрывает, кто именно его сыграл.',
        ],
        source: secretHitlerRules,
      },
      {
        title: 'Доказывает ли успешная миссия, что игроки добрые?',
        paragraphs: [
          'Допустим, Алиса и Борис отправляются на первую миссию в партии на пять человек. Чтобы отправить их, достаточно трёх голосов за. Если миссия провалится, в базовом наборе ролей хотя бы один из них злой. Если она успешна, любой из них всё ещё может быть злым и выбрать «Успех», чтобы заслужить доверие.',
          'Сопоставляйте их следующие голоса и предложенные команды с этим результатом. Повторно отправить успешную команду — решение, которое нужно обсудить, а не правило, подтверждающее, что оба игрока добрые. На четвёртой миссии при семи и более игроках для провала нужны две карты «Провал»; одна такая карта может оказаться в успешной миссии.',
        ],
      },
      {
        title: 'Мерлин и Гитлер: почему раскрытие роли имеет разный смысл',
        paragraphs: [
          'В Secret Hitler личность конкретного злого игрока важна для победы через выборы. В Авалоне добрый игрок Мерлин получает сведения о зле в начале партии. Он должен направлять команду добра, не выдавая своих знаний, потому что даже после трёх успешных миссий у зла остаётся шанс на убийство.',
          'Не переносите в Авалон привычку «добрые должны раскрывать всё». Уверенное объяснение может помочь пройти миссию и одновременно подсказать злу, кто Мерлин. В руководстве по роли объясняется, кого видит Мерлин и как дополнительные роли меняют эту информацию.',
        ],
      },
      {
        title: 'Когда Авалон подойдёт как альтернатива Secret Hitler?',
        paragraphs: [
          'Попробуйте Авалон, если вашей компании интересны команды разного размера, улики по результатам миссий и участие всех игроков до финала. Оставайтесь с Secret Hitler, если друзья больше всего ценят случайность законов и президентские полномочия. У обеих игр есть браузерные версии; сама возможность играть в браузере ещё не повод менять игру.',
          'Для обычной партии в Авалон здесь соберите 5–10 человек, попросите всех войти в аккаунты и начните с простого набора дополнительных ролей и расширений. Можно сначала освоиться с ботами. Прочитайте ниже об отличиях платформы, прежде чем считать, что отклонение команд и убийство работают точно как в настольном оригинале.',
        ],
        source: { label: 'Правила и браузерная версия Secret Hitler.io', url: 'https://secrethitler.io/rules' },
      },
      {
        title: 'Освойте голосование за миссии с ботами',
        paragraphs: [
          'Если ваши друзья по Secret Hitler ещё не в сети, попробуйте Авалон одному с ботами. Режим «Первая игра» с подсказками поможет освоить голосование за команду и карты миссий без сбора полной компании. Обратите внимание: успешная миссия может укрепить доверие, но не доказывает, что игрок добрый.',
          'В партиях с ботами вы также увидите, как работают Мерлин и финальное убийство, прежде чем объяснять их друзьям. Можно пригласить друзей и заполнить свободные места ботами. Число мест и режим выбираются при настройке: там показана текущая доступность и отмечено, для какого числа мест нужен Premium.',
        ],
      },
      {
        title: 'Можно ли играть в эту альтернативу Secret Hitler без скачивания?',
        paragraphs: [
          'Да. Авалон здесь работает в браузере без установки приложения. Для бесплатной обычной партии соберите 5–10 человек, попросите всех войти в аккаунты со своих устройств, создайте комнату и поделитесь ссылкой. Браузерный формат не делает игры одинаковыми: в Авалоне миссии и Мерлин заменяют колоду законов и полномочия правительства.',
          'Чтобы освоить отличия, полная компания не нужна. В настройках игры с AI выберите «Одному» или «Первая игра», либо «С друзьями», чтобы боты заняли свободные места. Нужен аккаунт; перед стартом проверьте текущую доступность и варианты числа мест, отмеченные Premium. У игры с AI свои условия доступа, отличающиеся от бесплатных обычных комнат с людьми.',
        ],
      },
    ],
  },
  avalon_vs_werewolf: {
    slug: 'avalon-vs-werewolf',
    breadcrumb: 'Авалон и настольная «Мафия»',
    title: 'Настольные игры, похожие на «Мафию»: Авалон и «Оборотни»',
    lead: 'Ищете настольные игры, похожие на «Мафию», без раннего выбывания? В Авалоне 5–10 друзей голосуют за команды на миссию, а не исключают подозреваемых. Сравните его с классическими «Оборотнями» и отдельным вариантом One Night Werewolf.',
    table: {
      caption: 'Классические «Мафия» и «Оборотни» (Werewolf) в сравнении с базовым Авалоном',
      columns: ['Часть игры', 'Классические «Оборотни» / «Мафия»', 'Авалон'],
      rows: [
        {
          title: 'Общее решение',
          cells: ['Голосование за исключение подозреваемого.', 'Одобрение или отклонение команды на миссию.'],
        },
        {
          title: 'Тайное действие',
          cells: [
            'Ночные действия, например нападения и проверки; подробности зависят от выбранных правил.',
            'Выбранные игроки разыгрывают карты миссии «Успех» или «Провал».',
          ],
        },
        {
          title: 'Если вас подозревают',
          cells: [
            'Выбывание может завершить ваше активное участие.',
            'Вы остаётесь в обсуждении и участвуете в каждом голосовании за команду.',
          ],
        },
        {
          title: 'Накопление улик',
          cells: [
            'Обсуждения, голоса, итоги ночи и сведения от ролей.',
            'Предложенные команды, открытые голоса за них и результаты миссий.',
          ],
        },
        {
          title: 'Ведение игры',
          cells: [
            'В классической настольной партии обычно нужен ведущий; онлайн-сервисы могут обрабатывать действия.',
            'Эта платформа управляет тайными ролями, голосованием и результатами миссий.',
          ],
        },
      ],
    },
    sections: [
      {
        title: 'Выбывание в классической «Мафии» и команды в Авалоне',
        paragraphs: [
          'В классических «Оборотнях» деревня обсуждает подозреваемых днём, а скрытый враг действует ночью. Выбывшие игроки обычно больше не участвуют в обсуждении и голосовании. У «Мафии» много местных вариантов правил, поэтому договоритесь о версии заранее: роли и условия победы могут отличаться.',
          'В Авалоне игрок, которого не взяли в команду, остаётся в игре. Он может спорить с предложенным составом, голосовать за него и стать следующим лидером. Даже игрок, которому никто не доверяет, способен повлиять на исход, поэтому компании приходится иначе обращаться с подозрениями.',
        ],
        source: werewolfRules,
      },
      {
        title: 'Что заменяет голосование за исключение подозреваемого?',
        paragraphs: [
          'Вместо того чтобы остановиться на «Алиса похожа на злую», спросите, какую команду вы готовы одобрить и почему. В партии на пять человек первая миссия требует двух участников, а одобрить их должны как минимум трое из всех пяти игроков. Только выбранная пара разыгрывает карты миссии; результат оценивают все.',
          'Карта «Провал» на миссии в базовой игре означает, что хотя бы один участник команды злой. Она не раскрывает, кто саботировал миссию. «Успех» не оправдывает обоих участников, ведь зло тоже может выбрать «Успех». Продолжайте сопоставлять команды и голоса каждого игрока, не превращая один результат в окончательный вывод.',
        ],
      },
      {
        title: 'Мерлин — не провидец с ночными проверками',
        paragraphs: [
          'Провидец в классических «Оборотнях» получает сведения через ночные действия. Мерлин в Авалоне получает информацию в начале партии, с исключениями для некоторых ролей, например Мордреда. В базовом Авалоне нет повторяющихся ночных проверок. Задача Мерлина — помогать добру, не раскрывая себя.',
          'После трёх проваленных миссий зло побеждает. После трёх успешных миссий зло всё ещё получает попытку убить Мерлина. Это последнее решение в игре, а не раннее выбывание, после которого партия продолжается без выбранной жертвы.',
        ],
      },
      {
        title: 'В One Night Werewolf тоже нет раннего выбывания?',
        paragraphs: [
          'В One Night Ultimate Werewolf уже нет раннего выбывания и не нужен ведущий. Игра охватывает одну ночь и последующее обсуждение, а не повторяющуюся смену дня и ночи, как в классических «Оборотнях». Если ваша компания уже играет в One Night, Авалон предложит последовательность голосований за команды и результатов миссий, но участие всех игроков до конца не будет его исключительным преимуществом.',
        ],
        source: oneNight,
      },
      {
        title: 'Когда выбрать Авалон, «Оборотней» или «Мафию»',
        paragraphs: [
          'Выбирайте обычную партию в Авалон с людьми ради непрерывного обсуждения, постоянного состава из 5–10 игроков и нескольких возможностей проверить команды. В партиях Авалона с ботами можно попробовать те же решения о миссиях одному или с друзьями. Выбирайте классических «Оборотней» или «Мафию», если вам нравятся ночные способности, исключение подозреваемых и меняющееся число участников. Онлайн-версии «Оборотней» (Werewolf) тоже существуют, в том числе браузерная версия по ссылке здесь.',
          'При переходе сначала объясните голосование за команду и карты миссий, затем Мерлина и убийство. Пользуйтесь таблицами числа игроков и ролей в наших правилах, а не переносите в Авалон баланс ролей из «Мафии».',
        ],
        source: browserWerewolf,
      },
      {
        title: 'Попробуйте Авалон с ботами до игры с друзьями по «Мафии»',
        paragraphs: [
          'Попробуйте режим «Одному» или «Первая игра» с подсказками, чтобы заменить привычное голосование за исключение на голосование за команду на миссию. Остальные места займут боты, а вы будете самостоятельно участвовать в обсуждении и принимать игровые решения. Обратите внимание: даже если вас не взяли на миссию, вы по-прежнему голосуете и наблюдаете за её результатом.',
          'На этой партии разберитесь, какую информацию Мерлин получает в начале и как принимается решение об убийстве, а затем объясните это друзьям по «Оборотням» (Werewolf) или «Мафии». Можно также пригласить друзей и заполнить свободные места ботами. Число мест, режим и поддерживаемый язык обсуждения выбираются при настройке, где показана текущая доступность.',
        ],
      },
      {
        title: 'Можно ли попробовать игру без выбывания онлайн и без установки?',
        paragraphs: [
          'Авалон здесь работает в браузере и управляет тайными ролями, голосами за команду и результатами миссий. Приложение и отдельный ведущий для обработки этих действий не нужны. Для обычной комнаты каждому из 5–10 игроков нужны аккаунт и своё устройство; такие партии бесплатны. Договоритесь о текстовом или голосовом общении до отправки ссылки на комнату.',
          'Чтобы проверить голосование за миссии до сбора компании по «Мафии», сыграйте одному с ботами или пригласите друзей, а свободные места заполните ботами. В настройках игры с AI показаны доступные сейчас режимы, поддерживаемые языки обсуждения и варианты числа мест с Premium. Для ботов нужен аккаунт, а доступность определяется отдельно; число мест в AI-партии проверяйте в настройках, отдельно от диапазона 5–10 для игры с людьми.',
        ],
      },
    ],
  },
};

export const copy: GuideCopy = {
  home: 'Главная',
  breadcrumbLabel: 'Навигационная цепочка',
  tocTitle: 'В этой статье',
  comparisonLabel: 'Сравнение игр',
  setupLabel: 'Условия и начало игры',
  aiAccessNote: 'Нужен аккаунт. Языки, режимы и доступ с Premium указаны при настройке.',
  setupTitle: 'Как играть в Авалон здесь: с друзьями или ботами',
  humanTitle: 'Обычные партии с друзьями',
  playersLabel: 'Игроки',
  humanPlayers: '5–10 человек, каждый со своего устройства.',
  accessLabel: 'Доступ',
  humanAccess: 'Бесплатные игры в браузере. Каждому игроку нужен аккаунт.',
  discussionLabel: 'Обсуждение',
  humanDiscussion: 'Текстовый чат и голосовой чат по желанию (бета).',
  findingGroupLabel: 'Поиск компании',
  findingGroup: 'Договоритесь о встрече с друзьями или воспользуйтесь досками игроков и групп.',
  boardsNote: 'Доски помогают договориться об игре, но не гарантируют немедленный подбор игроков.',
  aiTitle: 'Попробуйте одному или с друзьями и ботами',
  aiIntro:
    'Играйте одному или приглашайте друзей, а свободные места займут боты. Число мест и режим выбираются при настройке. Впервые в Авалоне? Попробуйте режим «Первая игра» с подсказками.',
  aiAvailability:
    'Нужен аккаунт. Обсуждения с ботами доступны на английском, русском, традиционном китайском, испанском и вьетнамском. Выберите режим при настройке: там показана текущая доступность и отмечено, для какого числа мест нужен Premium.',
  startLabel: 'Начните играть в Авалон',
  aiCta: 'Попробовать игру с ботами',
  lobbyCta: 'Открыть лобби Авалона',
  findGroupCta: 'Найти компанию',
  rulesCta: 'Изучить правила Авалона и голосование за миссии',
  sourceLabel: 'Источник',
  variantTitle: 'Два правила, которые нужно проверить перед первой игрой',
  proposalVariant:
    'В оригинальной настольной игре «Авалон» пять отклонённых команд подряд приносят победу злу. Здесь после четырёх отклонённых предложений пятая команда обычно отправляется на миссию без голосования; карты сюжета могут повлиять на её одобрение.',
  assassinationVariant:
    'В оригинальной игре после обсуждения со злом окончательный выбор Мерлина делает Убийца. В обычных комнатах с людьми здесь убийством управляет команда зла. В партиях с ботами перед решением об убийстве также проходит совет зла. Дополнительные и пользовательские роли могут менять доступные цели, поэтому проверьте настройки комнаты.',
  fullRulesCta: 'Прочитать правила Авалона, размеры команд и наборы ролей',
  previewAlt: 'Игровое поле Авалона с восемью местами игроков и результатами миссий',
  previewCaption: 'Поле Авалона с результатами миссий из существующего на сайте примера игрового процесса.',
  nextTitle: 'Начните с ботами или пригласите друзей',
  nextAi:
    'Хотите разобраться в игре до сбора полной компании? Нажмите «Играть с AI» в лобби, посмотрите доступные режимы и выберите «Одному» или «Первая игра» с подсказками. Чтобы пригласить друзей, выберите «С друзьями» и поделитесь ссылкой на подготовленную комнату: они смогут присоединиться до начала партии.',
  nextHuman:
    'Попросите 5–10 друзей создать аккаунты, договоритесь о языке и способе общения, затем откройте лобби и создайте комнату. Поделитесь её ссылкой с компанией. Впервые в Авалоне? Сначала объясните голосование за команду и карты миссий, а потом добавляйте дополнительные роли.',
  arrangeLabel: 'Сыграть или организовать партию в Авалон',
  browseGroupsCta: 'Посмотреть группы по Авалону',
  findPlayersCta: 'Найти игроков в Авалон',
  merlinCta: 'Узнать, кого видит Мерлин и как работает убийство',
  relatedLabel: 'Другие руководства по социальной дедукции',
  relatedTitle: 'Сравните Авалон с другими играми на социальную дедукцию',
  ownership:
    'Названия игр принадлежат их правообладателям. Это сравнение не означает, что они одобряют или поддерживают эту платформу.',
};
